/* eslint-disable @typescript-eslint/member-ordering */
import { useEffect, useMemo, useState } from 'react';
import {
  defaultConfig,
  FrontWebConfigKeyType,
  ValueType,
} from './defaultConfig';
import { getOpinionFilterCond } from '@op/services';
import { isEmpty } from '@gp/utils/common';

const cacheKey = 'opinion-front-web-config';

type FrontWebConfigType = {
  all_game: { key: FrontWebConfigKeyType; value: string }[];
  game: { key: FrontWebConfigKeyType; value: string }[];
};

type ListParamsType = {
  key: FrontWebConfigKeyType;
  defaultValue?: any;
}[];

type KeyParamsType = ListParamsType | FrontWebConfigKeyType;

function getValueType(data: any): ValueType {
  if (typeof data === 'string') return 'string';
  if (typeof data === 'number') return 'number';
  if (typeof data === 'boolean') return 'boolean';
  if (Array.isArray(data)) return 'array';
  if (typeof data === 'object') return 'object';
  return 'undefined';
}

/**
 * 获取默认值
 * 第一优先级为传入的默认值          defaultValue
 * 第二优先级为根据key配置的默认值   defaultConfig
 * @param keys
 * @param defaultValue
 * @param type
 * @returns
 */
function getDefaultValue(
  keys: KeyParamsType,
  defaultValue?: any,
): { [key in FrontWebConfigKeyType]: any } {
  if (typeof keys === 'string') return defaultValue || defaultConfig[keys];

  return keys.reduce((acc, { key, defaultValue }) => {
    const value = defaultValue || defaultConfig[key];
    return { ...acc, [key]: value };
  }, {} as { [key in FrontWebConfigKeyType]: any });
}

/**
 * 解析配置数据，并且缓存到本地
 * @param data
 * @returns 解析完成的数据
 */
export function formatFrontWebConfig(data: FrontWebConfigType) {
  let { all_game = [], game = [] } = data || {};

  all_game = Array.isArray(all_game) ? all_game : [];
  game = Array.isArray(game) ? game : [];
  const result: { [key: string]: any } = {};
  // 当前游戏配置 > 全局游戏配置
  [...all_game, ...game].forEach(({ key, value }) => {
    try {
      const json = JSON.parse(value);
      result[key] = json;
    } catch (error) {
      // console.log(error);
      result[key] = value;
    }
  });
  return result;
}
export default class DBO_FWC {
  private config: { [key: string]: any } = {};
  static frontWebConfig: DBO_FWC | null = null;

  private constructor(data: any) {
    this.config = data;
  }

  private setConfig(value: { [key: string]: any }) {
    this.config = value;
  }

  static init(data: FrontWebConfigType) {
    const value = formatFrontWebConfig(data);

    // 先进行缓存
    localStorage.setItem(cacheKey, JSON.stringify(value));

    if (DBO_FWC.frontWebConfig === null) {
      return (DBO_FWC.frontWebConfig = new DBO_FWC(value));
    }

    DBO_FWC.frontWebConfig.setConfig(value);
  }

  /**
   * 单个获取值
   * @param keys
   * @param type
   * @returns
   */
  getSingle(key: FrontWebConfigKeyType, defaultValue?: any): any {
    // 处理单个key的情况
    const value = this.config[key];

    const realDefaultValue = getDefaultValue(key, defaultValue);
    // 判断与默认值类型是否一致，不一致就返回默认值
    if (getValueType(realDefaultValue) !== getValueType(value)) {
      return realDefaultValue;
    }

    return value;
  }

  getList(keys: ListParamsType) {
    // 处理数组参数的情况
    return keys.reduce((acc, { key, defaultValue }) => {
      acc[key] = this.getSingle(key, defaultValue);
      return acc;
    }, {} as Record<FrontWebConfigKeyType, any>);
  }

  static addConfig(obj: { [key in FrontWebConfigKeyType]: any }) {
    if (DBO_FWC.frontWebConfig) {
      DBO_FWC.frontWebConfig.config = {
        ...DBO_FWC.frontWebConfig?.config,
        ...obj,
      };
    }
  }

  static get(keys: KeyParamsType, defaultValue?: any) {
    if (DBO_FWC.frontWebConfig === null) {
      return getDefaultValue(keys, defaultValue);
    }

    if (typeof keys === 'string') {
      return DBO_FWC.frontWebConfig.getSingle(keys, defaultValue);
    }

    return DBO_FWC.frontWebConfig.getList(keys);
  }

  static hasKeys(keys: FrontWebConfigKeyType[]) {
    return keys.every(key => !isEmpty(DBO_FWC.frontWebConfig?.config[key]));
  }
}

/**
 * 单key返回单值，多key返回多个值的对象
 * @param param0
 * @returns
 */
export function useFrontWebConfig({
  keys,
  defaultValue,
}: {
  keys: KeyParamsType;
  defaultValue?: any;
}) {
  const result = useMemo(() => {
    return DBO_FWC.get(keys, defaultValue);
  }, []);

  return result;
}

/**
 * 通过请求获取前端配置
 * 只能获取配置对象
 * @param param0
 * @returns
 */
export function useRequestFontWebConfig({
  keys,
  gameInfo,
  needRequest = true,
}: {
  keys: ListParamsType;
  gameInfo: any;
  needRequest?: boolean;
}) {
  const [loading, setLoading] = useState(true);

  function getDefaultResult(): { [key in FrontWebConfigKeyType]: any } {
    return getDefaultValue(keys);
  }

  const [result, setResult] = useState<{ [key in FrontWebConfigKeyType]: any }>(
    getDefaultResult(),
  );

  useEffect(() => {
    const requestKeys = keys.map(item => item.key);
    // 先查找缓存
    if (DBO_FWC.hasKeys(requestKeys)) {
      setResult(DBO_FWC.get(keys));
      setLoading(false);
      return;
    }
    if (needRequest) {
      // 再请求接口
      getOpinionFilterCond({
        ...gameInfo,
        un_request_type: ['topic_trans'],
        request_type: ['front_web_config'],
        sub_request_type: requestKeys,
      })
        .then((data: any) => {
          const value: any = formatFrontWebConfig(data.front_web_config || {});
          DBO_FWC.addConfig(value);
          const config = DBO_FWC.get(keys);
          setResult({ ...config, ...value });
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, []);

  return { ...result, loading };
}
